import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, ViewChildren, } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormGroupDirective } from '@angular/forms';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, filter, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';
import { UserViewModel } from '../../../shared/interfaces/viewmodels/userviewmodel';
import { AccountState } from '../../../shared/store/state/account.state';
import { AssignedSubApplications, IORD_Order, ORD_Order, PatchORDOrderDto, ReturnViewModel, RTAOptions } from '../../../shared/interfaces/request.interface';
import { GroupResult, groupBy } from "@progress/kendo-data-query";
import { OptionState } from '../../../shared/store/state/options.state';
import { handIcon, eyeIcon, downloadIcon, SVGIcon } from "@progress/kendo-svg-icons";
import { RequestActions } from '../../../shared/store/action/request.action';
import { GlobalNotificationService } from '../../../shared/services/globalnotification.service';
import { RequestState } from '../../store/state/request.state';
import { ActivatedRoute } from '@angular/router';
import { OptionActions } from '../../store/action/options.action';
import { RtaService } from '../../services/rta.service';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { encodeHtmlForTransit } from '../../utils/HtmlEncoder';
import { FileVaultApiService } from '../../services/filevault-api.service';

@Component({
    selector: 'app-basic-data',
    templateUrl: './basic-data.component.html',
    styleUrl: './basic-data.component.css',
    standalone: false
})
export class BasicDataComponent {
  @Input() draftId?: number;
  @Input() initial?: IORD_Order;
  @Input() readonly: boolean = false;
  @Output() productLineChanged = new EventEmitter<number>();
  @Output() referenceImported = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  @ViewChild(FormGroupDirective, { static: true })
  public formDir!: FormGroupDirective;

   public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "startsWith",
  };

  /** Marks all controls touched, re-evaluates validity, and returns true if valid */
  public validateForm(): boolean {
    // Re-run validity so dynamic validators are up-to-date
    this.appBasicData.updateValueAndValidity();
    Object.values(this.appBasicData.controls).forEach(ctrl => {
      ctrl.updateValueAndValidity({ onlySelf: true });
    });
    this.formDir.form.markAllAsTouched();
    return this.appBasicData.valid;
  }
  constructor(private fb: FormBuilder, private store: Store, private route: ActivatedRoute,
    private globalNotificationService: GlobalNotificationService, private rtaService: RtaService,
    private fileVaultApi: FileVaultApiService) {
    this.appBasicData = this.fb.group({
      requiredDate: [null],
      productLine: [null, Validators.required],
      applicationId: [null],
      activeProblems: [[]],
      Reference: [0],
      nwA_ApplicationId: [null],
      priority: [4],
      ToolSize: [null],
      notesToDesignSupport: [''],
      description: [''],
      difference: [''],
      Region: [null],
      requestExclusion: [false],
      targetCustomer: [''],
      customerRequirements: [[]],
    });
  }
  public user$!: Observable<UserViewModel>;
  public subApplications$!: Observable<AssignedSubApplications[]>;
  public groupedSubApplications$!: Observable<GroupResult[]>;
  public rtaOptions$!: Observable<RTAOptions[]>;
  public orderId: number = 0;

  public eyeIcon: SVGIcon = eyeIcon;
  public handIcon: SVGIcon = handIcon;
  public importIcon: SVGIcon = downloadIcon;
  public isImporting: boolean = false;
  public referenceLocked: boolean = false;

  public Request: Request = {} as Request;

  public ProductLines_groupedData$!: Observable<GroupResult[]>;
  public subApplication_groupedData$!: Observable<GroupResult[]>;
  public ProductLines_List$!: Observable<any[]>;
  public SubApplications_List$!: Observable<any[]>;

  // Filtered display data for grouped dropdowns (manual filter)
  private _allProductLines: any[] = [];
  private _allSubApplications: any[] = [];
  public productLinesFiltered: GroupResult[] = [];
  public subApplicationsFiltered: GroupResult[] = [];
  public activeProblemOptions$!: Observable<any[]>;
  public Type_Data$!: Observable<any[]>;
  public Type_Data_SubPSLFiltered$!: Observable<any[]>;
  public priorityOptions$!: Observable<any[]>;

  public MudTypes_Data: any[] = [];
  public Electronics_Data: any[] = [];
  public Uppersections_Data: any[] = [];
  public BitFeatures_Data: any[] = [];
  public Hardfacing_Data: any[] = [];
  public init_CustomerRequirements: string[]=[];

  public appBasicData!: FormGroup;
  public min: Date = this.normalizeDate(new Date());
  public yesno: Array<any> = [
    { value: false, name: "No" },
    { value: true, name: "Yes" }
  ];
  private readonly today = this.normalizeDate(new Date());
  public toolSizeOptions = [
    { text: '400', value: 400 },
    { text: '475', value: 475 },
    // ...etc
  ];
  public regionOptions = [
    { text: 'Permian', value: 166 },
    { text: 'Technology', value: 1605 },
    // ...etc
  ];

  public showRegion = false;
  public showToolSize = false;

  ngOnInit(): void {
    this.user$ = this.store.select(AccountState.user);
    this.rtaOptions$ = this.store.select(OptionState.options);
    this.ProductLines_List$ = this.store.select(OptionState.productlines);
    this.ProductLines_groupedData$ = this.store.select(OptionState.productLines_groupedData);
    this.SubApplications_List$ = this.store.select(OptionState.subApplication);
    this.subApplication_groupedData$ = this.store.select(OptionState.subApplications_groupedData);

    // Seed flat caches for manual filtering on grouped dropdowns
    // Use takeUntil (not take(1)) so they update when GetOptions populates the store
    this.store.select(OptionState.productlines).pipe(takeUntil(this.destroy$))
      .subscribe(data => { this._allProductLines = data; this.productLinesFiltered = groupBy(data, [{ field: 'subcategory' }]) as GroupResult[]; });
    this.store.select(OptionState.subApplication).pipe(takeUntil(this.destroy$))
      .subscribe(data => { this._allSubApplications = data; this.subApplicationsFiltered = groupBy(data, [{ field: 'subcategory' }]) as GroupResult[]; });
    this.priorityOptions$ = this.store.select(OptionState.priorities);
    this.Type_Data$ = this.store.select(OptionState.rtaType);
    this.activeProblemOptions$ = this.store.select(OptionState.activeChallenge);

    this.init_CustomerRequirements = this.initial?.customerRequirements?.map(x=>x.name??'')??[]

    const requestedDateCtrl = this.appBasicData.get('requiredDate');
    requestedDateCtrl?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(date => this.updateMinDate(date));

    // Check if the reference part is locked (checked out) when the value changes
    this.appBasicData.get('Reference')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter(val => val && val > 0),
      switchMap(val => this.fileVaultApi.isPartLocked(val)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (isLocked) => {
        this.referenceLocked = isLocked;
        if (isLocked) {
          this.globalNotificationService.showWarningNotification(
            'Warning: This reference part is currently checked out for revision. Using it may cause delays if the design changes before your request is completed.'
          );
        }
      },
      error: () => {
        this.referenceLocked = false;
      }
    });

    this.appBasicData.valueChanges.pipe(
      debounceTime(200),
      takeUntil(this.destroy$)
    ).subscribe(changes => {
      const serverChanges = this.toServerChanges(changes);
      const stateChanges = this.toStateChanges(changes);
      this.store.dispatch(new RequestActions.PatchRequestDraft({
        draftId: this.draftId,
        changes: serverChanges
      }));

      this.store.dispatch(new RequestActions.PatchRequest(stateChanges));
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Handle readonly state changes
    if (changes['readonly']) {
      if (this.readonly) {
        this.appBasicData.disable();
      } else {
        this.appBasicData.enable();
      }
    }
    
    if (changes['initial'] && this.initial) {
      const refNum = this.initial.sapNumbers
        ?.find(x => x.sapNumTypeId === 1)
        ?.number ?? 0;
      const CustomerRequirements = this.initial?.customerRequirements?.map(x=>x.name??'')??[]
      const normalizedRequiredDate = this.initial.requiredDate
        ? this.normalizeDate(new Date(this.initial.requiredDate))
        : null;
      this.appBasicData.patchValue({
        requiredDate: normalizedRequiredDate,
        productLine: this.initial.productLine,
        applicationId: this.initial.applicationId,
        activeProblems: this.initial.activeProblems,
        nwA_ApplicationId: this.initial.nwA_ApplicationId,
        priority: this.initial.priority,
        notesToDesignSupport: this.initial.notesToDesignSupport ?? '',
        description: this.initial.description ?? '',
        difference: this.initial.difference ?? '',
        requestExclusion: this.initial.requestExclusion,
        targetCustomer: this.initial.targetCustomer ?? '',
        customerRequirements: CustomerRequirements ?? [],
        Reference: refNum
      });
      this.updateMinDate(normalizedRequiredDate ?? null);
      
      // Re-apply readonly state after patching values
      if (this.readonly) {
        this.appBasicData.disable();
      }
    }
  }
  public addToDataSource(item: any): void {
    item.Qty++;
  }

  public subtractFromDataSource(item: any): void {
    if (item.Qty > 0) {
      item.Qty--;
    }
  }
  public openRefmatnum(): void {
    const refValue = this.appBasicData.get('Reference')?.value;
    if (!refValue || refValue === 0) {
      this.globalNotificationService.showErrorNotification('Please enter a reference material number first.');
      return;
    }
    const selectedPL = this.appBasicData.get('productLine')?.value;
    const subPSL = this._allProductLines.find(pl => pl.value === selectedPL)?.subcategory ?? '';
    var url = `/Bit/Details/`;
    if (subPSL === 'DHT')
      url = `/DHT/ReamerDetails/`;
    window.open(`http://hdbstech/Adesc${url}${refValue}`, '_blank');
  }

  public importReference(): void {
    const refValue = this.appBasicData.get('Reference')?.value;
    if (!refValue || refValue === 0) {
      this.globalNotificationService.showErrorNotification('Please enter a valid reference material number before importing.');
      return;
    }
    if (!this.draftId || this.draftId === 0) {
      this.globalNotificationService.showErrorNotification('Please save the order first before importing reference data.');
      return;
    }
    this.isImporting = true;
    this.rtaService.importReferenceData(this.draftId, refValue).pipe(take(1)).subscribe({
      next: (result) => {
        this.isImporting = false;
        if (result.status) {
          this.globalNotificationService.showSuccessNotification('Reference data imported successfully. The bit data has been updated.');
          // Refresh order data in the store so all tabs reflect the imported data
          this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.draftId!.toString() }));
          this.referenceImported.emit();
        } else {
          this.globalNotificationService.showErrorNotification(result.message ?? 'Failed to import reference data.');
        }
      },
      error: (err) => {
        this.isImporting = false;
        this.globalNotificationService.showErrorNotification('Error importing reference data. Please try again.');
      }
    });
  }
  public handleProductlineChange(value: any) {
    const currentPLValue = this.initial?.productLine;
    const currentSubPSL = this._allProductLines.find(pl => pl.value === currentPLValue)?.subcategory ?? '';
    const newSubPSL = this._allProductLines.find(pl => pl.value === value)?.subcategory ?? '';

    if (currentSubPSL && newSubPSL && currentSubPSL !== newSubPSL) {
      this.globalNotificationService.showWarningNotification(
        'The product line sub-category has changed. Please save the form first in order to load the new sub-PSL forms.'
      );
    }

    this.productLineChanged.emit(value);
  }

  public onProductLineFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? this._allProductLines.filter(item => item.name?.toLowerCase().includes(term))
      : this._allProductLines;
    this.productLinesFiltered = groupBy(filtered, [{ field: 'subcategory' }]) as GroupResult[];
  }

  public onSubApplicationFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? this._allSubApplications.filter(item =>
          item.name?.toLowerCase().includes(term) ||
          item.subcategory?.toLowerCase().includes(term)
        )
      : this._allSubApplications;
    this.subApplicationsFiltered = groupBy(filtered, [{ field: 'subcategory' }]) as GroupResult[];
  }

  getFilteredTypes(): Observable<any[]>{
    this.appBasicData.value.requestType = 0;
    this.ProductLines_List$.pipe(take(1)).subscribe(productLines => {
      let selectedProductline = productLines.filter((p) => p.value === this.appBasicData.get('productLine')?.value);
      this.Type_Data_SubPSLFiltered$ = this.Type_Data$.pipe(
        map(data => data.filter(
          (t) => t.permissionid === selectedProductline[0]?.permissionID
        ))
      );
    });
    return this.Type_Data_SubPSLFiltered$;
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateMinDate(selectedDate: Date | string | null | undefined): void {
    const normalizedSelected = selectedDate ? this.normalizeDate(new Date(selectedDate)) : null;
    if (normalizedSelected && normalizedSelected < this.today) {
      this.min = normalizedSelected;
    } else {
      this.min = this.today;
    }
  }

  private normalizeDate(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private toServerChanges(changes: any): any {
    if (!changes) {
      return changes;
    }
    const sanitized = { ...changes };
    if (changes.requiredDate) {
      const normalized = this.normalizeDate(new Date(changes.requiredDate));
      sanitized.requiredDate = this.formatDateOnly(normalized);
    }
    // Base64-encode HTML fields so Azure WAF does not block the request
    if (sanitized.description != null) {
      sanitized.description = encodeHtmlForTransit(sanitized.description);
    }
    if (sanitized.notesToDesignSupport != null) {
      sanitized.notesToDesignSupport = encodeHtmlForTransit(sanitized.notesToDesignSupport);
    }
    return sanitized;
  }

  private toStateChanges(changes: any): any {
    if (!changes) {
      return changes;
    }
    const sanitized = { ...changes };
    if (changes.requiredDate) {
      sanitized.requiredDate = this.normalizeDate(new Date(changes.requiredDate));
    }
    return sanitized;
  }

  private formatDateOnly(date: Date | null | undefined): string | null {
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
